import React, { useState, useEffect } from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { toast } from 'react-toastify';
import {
  createGiftCard,
  getAllGiftCards,
  updateGiftCard,
  deleteGiftCard,
  getGiftCardUsage,
} from '../../services/adminApi';

const GiftCardsPage = () => {
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGiftCard, setSelectedGiftCard] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [giftCardUsage, setGiftCardUsage] = useState([]);
  const [formData, setFormData] = useState({
    initialValue: '',
    description: '',
    isActive: true,
  });

  const fetchGiftCards = async () => {
    try {
      setLoading(true);
      const response = await getAllGiftCards();
      if (response.success) {
        setGiftCards(response.giftCards);
      } else {
        setError('Failed to fetch gift cards.');
      }
    } catch (err) {
      setError('An error occurred while fetching gift cards.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGiftCards();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleFormSubmit = async () => {
    try {
      if (selectedGiftCard) {
        await updateGiftCard(selectedGiftCard._id, formData);
        toast.success('Gift card updated successfully');
      } else {
        await createGiftCard(formData);
        toast.success('Gift card created successfully');
      }
      setModalOpen(false);
      fetchGiftCards();
    } catch (error) {
      toast.error('Failed to save gift card');
    }
  };

  const handleDeleteGiftCard = async () => {
    try {
      await deleteGiftCard(selectedGiftCard._id);
      toast.success('Gift card deleted successfully');
      setDeleteModalOpen(false);
      fetchGiftCards();
    } catch (error) {
      toast.error('Failed to delete gift card');
    }
  };

  const openModal = (giftCard = null) => {
    setSelectedGiftCard(giftCard);
    setFormData(
      giftCard
        ? {
            description: giftCard.description,
            isActive: giftCard.isActive,
          }
        : {
            initialValue: '',
            description: '',
            isActive: true,
          }
    );
    setModalOpen(true);
  };

  const openDeleteModal = (giftCard) => {
    setSelectedGiftCard(giftCard);
    setDeleteModalOpen(true);
  };

  const openViewModal = async (giftCard) => {
    setSelectedGiftCard(giftCard);
    try {
      const response = await getGiftCardUsage(giftCard._id);
      if (response.success) {
        setGiftCardUsage(response.orders);
      } else {
        toast.error('Failed to fetch gift card usage');
      }
    } catch (error) {
      toast.error('An error occurred while fetching gift card usage');
    }
    setViewModalOpen(true);
  };

  if (loading) {
    return <p>Loading gift cards...</p>;
  }

  if (error) {
    return <p className="text-red-500">{error}</p>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Gift Cards</CardTitle>
          <Button onClick={() => openModal()}>Add Gift Card</Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Initial Value</TableHead>
                <TableHead>Current Value</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Purchased By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {giftCards.map((giftCard) => (
                <TableRow key={giftCard._id}>
                  <TableCell>{giftCard.code}</TableCell>
                  <TableCell>£{giftCard.initialValue.toFixed(2)}</TableCell>
                  <TableCell>£{giftCard.currentValue.toFixed(2)}</TableCell>
                  <TableCell>{giftCard.description || '-'}</TableCell>
                  <TableCell>
                    {giftCard.purchasedBy ? (
                      <div>
                        <div>{giftCard.purchasedBy.email || giftCard.purchaseEmail || 'N/A'}</div>
                        {giftCard.purchasedBy.firstName && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {giftCard.purchasedBy.firstName} {giftCard.purchasedBy.lastName}
                          </div>
                        )}
                      </div>
                    ) : (
                      'Admin Created'
                    )}
                  </TableCell>
                  <TableCell>{giftCard.isActive ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => openModal(giftCard)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openDeleteModal(giftCard)}>
                      Delete
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openViewModal(giftCard)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Gift Card Modal */}
      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedGiftCard ? 'Edit Gift Card' : 'Add Gift Card'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {!selectedGiftCard && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="initialValue" className="text-right">
                  Initial Value
                </Label>
                <Input
                  id="initialValue"
                  name="initialValue"
                  type="number"
                  value={formData.initialValue}
                  onChange={handleInputChange}
                  className="col-span-3"
                />
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Input
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="isActive" className="text-right">
                Status
              </Label>
              <Select
                onValueChange={(value) => handleSelectChange('isActive', value)}
                defaultValue={formData.isActive}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={true}>Active</SelectItem>
                  <SelectItem value={false}>Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleFormSubmit}>Save</Button>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Gift Card Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Gift Card</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this gift card?</p>
          <DialogFooter>
            <Button variant="destructive" onClick={handleDeleteGiftCard}>
              Delete
            </Button>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Gift Card Usage Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gift Card Usage</DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order Number</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Amount Used</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {giftCardUsage.map((order) => (
                <TableRow key={order._id}>
                  <TableCell>{order.orderNumber}</TableCell>
                  <TableCell>{order.user.email}</TableCell>
                  <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>{order.giftCardDiscount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftCardsPage;
